import { useEffect, useMemo, useState } from "react";
import {
  Trophy,
  Users,
  Target,
  Zap,
  FileText,
  ClipboardList,
  TrendingUp,
  Award,
  Search,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import { useAdminStore } from "../store/useAdminStore";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const initials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const PROFILES = ["Irfan Baig", "Nadeem", "Farah", "Rizwan"];

const TopStat = ({
  label,
  value,
  sub,
  icon: Icon,
  color = "blue",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  color?: "blue" | "emerald" | "amber" | "violet";
}) => {
  const colors = {
    blue: "from-blue-50 to-blue-100/40 text-blue-700",
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700",
    amber: "from-amber-50 to-amber-100/40 text-amber-700",
    violet: "from-violet-50 to-violet-100/40 text-violet-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em]">
          {label}
        </span>
        <div className={`w-11 h-11 grid place-items-center rounded-xl bg-gradient-to-br ${colors[color]}`}>
          <Icon size={20} />
        </div>
      </div>
      <p className="text-[32px] font-black leading-none tracking-tight text-slate-900">
        {value}
      </p>
      {sub && (
        <p className="text-[12px] text-slate-500 mt-2 font-medium">{sub}</p>
      )}
    </div>
  );
};

const AdminPerformancePage = () => {
  const { bdPerformance, fetchBdPerformance } = useAdminStore();
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchBdPerformance();
  }, [fetchBdPerformance]);

  const filtered = bdPerformance.filter((bd) =>
    !search
      ? true
      : bd.name.toLowerCase().includes(search.toLowerCase()) ||
        bd.email.toLowerCase().includes(search.toLowerCase())
  );

  const totals = useMemo(() => {
    return bdPerformance.reduce(
      (acc, bd) => {
        acc.revenue += bd.totalRevenue;
        acc.projects += bd.totalProjects;
        acc.bids += bd.totalBids;
        acc.connects += bd.totalConnects;
        acc.proposals += bd.proposalsCreated;
        acc.questionnaires += bd.questionnairesCreated;
        acc.jobsWon += bd.jobsWon;
        return acc;
      },
      {
        revenue: 0,
        projects: 0,
        bids: 0,
        connects: 0,
        proposals: 0,
        questionnaires: 0,
        jobsWon: 0,
      }
    );
  }, [bdPerformance]);

  const topPerformer = bdPerformance[0];
  const avgWinRate = bdPerformance.length
    ? Math.round(
        bdPerformance.reduce((s, b) => s + b.winRate, 0) / bdPerformance.length
      )
    : 0;

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              Business Developer Performance
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Track every BD's revenue, bids, win rate & activity — across all
              4 Upwork profiles.
            </p>
          </div>
          <div className="relative w-72">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search BD by name or email..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <TopStat
            label="Total Revenue (All BDs)"
            value={fmtMoney(totals.revenue)}
            sub={`${totals.projects} projects across team`}
            icon={TrendingUp}
            color="blue"
          />
          <TopStat
            label="Bids Submitted"
            value={totals.bids.toLocaleString()}
            sub={`${totals.jobsWon} won · avg win ${avgWinRate}%`}
            icon={Target}
            color="amber"
          />
          <TopStat
            label="Proposals + Questionnaires"
            value={(totals.proposals + totals.questionnaires).toLocaleString()}
            sub={`${totals.proposals} proposals · ${totals.questionnaires} questionnaires`}
            icon={FileText}
            color="violet"
          />
          <TopStat
            label="Top Performer"
            value={topPerformer ? topPerformer.name.split(" ")[0] || "—" : "—"}
            sub={
              topPerformer
                ? `${fmtMoney(topPerformer.totalRevenue)} generated`
                : "No BDs yet"
            }
            icon={Trophy}
            color="emerald"
          />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-[18px] font-bold text-slate-900">
                BD Leaderboard
              </h3>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Sorted by total revenue generated · per-profile breakdown
                included
              </p>
            </div>
            <div className="flex items-center gap-2 text-[12px] text-slate-500">
              <Users size={14} />
              <span>{bdPerformance.length} active BDs</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <Th>Rank</Th>
                  <Th>Business Developer</Th>
                  <Th align="right">Revenue</Th>
                  <Th align="center">Projects</Th>
                  <Th align="center">Bids</Th>
                  <Th align="center">Win Rate</Th>
                  <Th align="center">Connects</Th>
                  <Th align="center">Proposals</Th>
                  <Th align="center">Quest.</Th>
                  <Th align="center">Clients</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-14 text-center text-slate-400 text-[13px]"
                    >
                      No business developers found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((bd, i) => (
                    <tr
                      key={bd.bdId}
                      className="hover:bg-slate-50/50 transition"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <RankBadge rank={i + 1} />
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[12px] font-bold shadow-sm">
                            {initials(bd.name)}
                          </div>
                          <div>
                            <p className="text-[13px] font-bold text-slate-900">
                              {bd.name}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {bd.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <p className="text-[14px] font-extrabold text-[#006a61]">
                          {fmtMoney(bd.totalRevenue)}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          {bd.completedProjects} closed
                        </p>
                      </td>
                      <td className="px-4 py-4 text-center text-[13px] text-slate-700 font-semibold">
                        {bd.totalProjects}
                      </td>
                      <td className="px-4 py-4 text-center text-[13px] text-slate-700 font-semibold">
                        {bd.totalBids}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <WinRatePill rate={bd.winRate} />
                      </td>
                      <td className="px-4 py-4 text-center text-[13px] text-slate-700 font-semibold">
                        {bd.totalConnects}
                      </td>
                      <td className="px-4 py-4 text-center text-[13px] text-slate-700 font-semibold">
                        {bd.proposalsCreated}
                      </td>
                      <td className="px-4 py-4 text-center text-[13px] text-slate-700 font-semibold">
                        {bd.questionnairesCreated}
                      </td>
                      <td className="px-4 py-4 text-center text-[13px] text-slate-700 font-semibold">
                        {bd.clientsManaged}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Zap size={18} className="text-[#0058c3]" />
              <h3 className="text-[18px] font-bold text-slate-900">
                Per-Profile Contribution
              </h3>
            </div>
            <p className="text-[12px] text-slate-500 mt-1">
              Kis BD ne kis Upwork profile per kitna generate kya — revenue
              breakdown.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <Th>Business Developer</Th>
                  {PROFILES.map((p) => (
                    <Th key={p} align="right">
                      {p}
                    </Th>
                  ))}
                  <Th align="right">Total</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((bd) => (
                  <tr key={bd.bdId} className="hover:bg-slate-50/50 transition">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[10px] font-bold">
                          {initials(bd.name)}
                        </div>
                        <span className="text-[13px] font-semibold text-slate-900">
                          {bd.name}
                        </span>
                      </div>
                    </td>
                    {PROFILES.map((name) => {
                      const v = bd.perProfile[name];
                      const rev = v?.revenue || 0;
                      const isBest =
                        Math.max(
                          ...PROFILES.map(
                            (n) => bd.perProfile[n]?.revenue || 0
                          )
                        ) === rev && rev > 0;
                      return (
                        <td key={name} className="px-4 py-4 text-right">
                          <div
                            className={`inline-flex flex-col items-end ${
                              isBest ? "text-[#006a61]" : "text-slate-700"
                            }`}
                          >
                            <span className="text-[13px] font-bold">
                              {fmtMoney(rev)}
                            </span>
                            <span className="text-[10px] text-slate-500">
                              {v?.projects || 0}p · {v?.bids || 0}b
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-4 text-right">
                      <span className="text-[14px] font-extrabold text-[#0058c3]">
                        {fmtMoney(bd.totalRevenue)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ActivityCard
            icon={FileText}
            title="Proposals Created"
            data={filtered
              .filter((b) => b.proposalsCreated > 0)
              .slice(0, 5)
              .map((b) => ({
                name: b.name,
                count: b.proposalsCreated,
              }))}
            empty="No proposals created yet."
          />
          <ActivityCard
            icon={ClipboardList}
            title="Questionnaires Created"
            data={filtered
              .filter((b) => b.questionnairesCreated > 0)
              .slice(0, 5)
              .map((b) => ({
                name: b.name,
                count: b.questionnairesCreated,
              }))}
            empty="No questionnaires created yet."
          />
          <ActivityCard
            icon={Award}
            title="Highest Win Rates"
            data={[...filtered]
              .sort((a, b) => b.winRate - a.winRate)
              .slice(0, 5)
              .map((b) => ({
                name: b.name,
                count: b.winRate,
                suffix: "%",
              }))}
            empty="No bid activity yet."
          />
        </div>
      </div>
    </AdminLayout>
  );
};

const Th = ({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) => (
  <th
    className={`px-4 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-${align}`}
  >
    {children}
  </th>
);

const RankBadge = ({ rank }: { rank: number }) => {
  const styles =
    rank === 1
      ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-md"
      : rank === 2
      ? "bg-gradient-to-br from-slate-300 to-slate-500 text-white"
      : rank === 3
      ? "bg-gradient-to-br from-orange-300 to-orange-500 text-white"
      : "bg-slate-100 text-slate-600";
  return (
    <span
      className={`w-7 h-7 grid place-items-center rounded-full text-[12px] font-extrabold ${styles}`}
    >
      {rank}
    </span>
  );
};

const WinRatePill = ({ rate }: { rate: number }) => {
  const cls =
    rate >= 50
      ? "bg-emerald-100 text-emerald-700"
      : rate >= 25
      ? "bg-amber-100 text-amber-700"
      : rate > 0
      ? "bg-rose-100 text-rose-700"
      : "bg-slate-100 text-slate-500";
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${cls}`}
    >
      {rate}%
    </span>
  );
};

const ActivityCard = ({
  icon: Icon,
  title,
  data,
  empty,
}: {
  icon: any;
  title: string;
  data: { name: string; count: number; suffix?: string }[];
  empty: string;
}) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-md bg-blue-50 grid place-items-center text-[#0058c3]">
        <Icon size={16} />
      </div>
      <h4 className="text-[14px] font-bold text-slate-900">{title}</h4>
    </div>
    {data.length === 0 ? (
      <p className="text-[12px] text-slate-400 text-center py-6">{empty}</p>
    ) : (
      <div className="space-y-2.5">
        {data.map((row, i) => (
          <div
            key={i}
            className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[9px] font-bold">
                {initials(row.name)}
              </div>
              <span className="text-[12px] text-slate-700 font-medium">
                {row.name}
              </span>
            </div>
            <span className="text-[13px] font-extrabold text-slate-900">
              {row.count}
              {row.suffix || ""}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default AdminPerformancePage;
